
import {
  computed, defineComponent, Ref, ref,
} from 'vue';
import { useRoute } from 'vue-router';
import { IMedia } from '@/types/interfaces';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '../../store/driveStore';
import {
  FOLDER_SETTINGS_POP_UP_SWITCHER,
  MEDIA_TYPES, NAVIGATION_PAGES, PHOTO_VIEW_LAYOUT, PHOTO_VIEW_TYPE, SHARE_SETTINGS,
} from '@/types/enums';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import { lowResUrlByViewTypeByUrl, lowResUrlByViewType } from '@/helpers/lowResPhotoUrl';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import MixedMediaPopUp from '@/components/BottomPopUp/PopUpTypes/MixedMediaPopUp.vue';
import AppRenameFile from '@/components/AppRenameFile.vue';

export default defineComponent({
  emits: ['on-select'],
  props: {
    photo: { type: Object as () => IMedia, required: true },
    hideTitle: { type: Boolean, default: false },
    selector: { type: Boolean, default: false },
    hideDelete: { type: Boolean, default: false },
    columnView: { type: Boolean, default: false },
    blockGrid: { type: Boolean, default: false }, // TODO rework it normally
    isParentSelect: { type: Boolean, default: false },
    selectedId: { type: String, default: '' },
    preventOpen: { type: Boolean, default: false },
    isMixed: { type: Boolean, default: false },
    isHmed: { type: Boolean, default: false },
    noClickAction: { type: Boolean, default: false },
    showDots: { type: Boolean, default: true },
  },
  components: {
    AppRenameFile,
    MixedMediaPopUp,
    AppQuestionPopUp,
    AppLazyImage,
  },
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const route = useRoute();
    const isLoading = ref(true);
    const popUpSwitcher: Ref<FOLDER_SETTINGS_POP_UP_SWITCHER | null> = ref(null);

    const dataLoading: Ref<string> = ref('');
    const isQuestionPopUp: Ref<boolean> = ref(false);
    const isMediaSettingsPopUp = ref(false);
    const fileRenamePopUp = ref(false);
    const renameMountErrorInfo = ref<undefined | {title: string, error: string}>();
    const isNetwork = computed(() => route.meta.isNetwork);

    const showMenuDots = computed(() => {
      if (props.showDots) {
        return isNetwork.value || props.columnView;
      }
      if (!props.showDots) {
        return false;
      }
      return true;
    });

    const currentPage = computed(() => route.meta.navigationPage);
    const isDeleteButton = computed(() => !props.hideDelete && !isNetwork.value && !(driveStore.photoViewLayout === PHOTO_VIEW_LAYOUT.GRID) && !props.columnView);
    const reshareAllowed = computed(() => props.photo.shareSettings?.includes(SHARE_SETTINGS.RESHARE_ALLOWED));

    const photoUrl = computed(() => {
      if (props.photo.type === MEDIA_TYPES.VIDEO) {
        return lowResUrlByViewType(props.photo, PHOTO_VIEW_TYPE.THUMB);
      }
      if (props.photo.type === MEDIA_TYPES.PHOTO) {
        if (!props.isHmed && !props.blockGrid && !props.columnView) {
          return lowResUrlByViewType(props.photo, PHOTO_VIEW_TYPE.WHMED);
        }
        return lowResUrlByViewType(props.photo, props.isHmed ? PHOTO_VIEW_TYPE.WHMED : PHOTO_VIEW_TYPE.WHSML);
      }

      return '';
    });

    const showTitle = computed(() => {
      if (props.hideTitle) return false;
      if (currentPage.value === NAVIGATION_PAGES.DRIVE_FILES
       || currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER
       || currentPage.value === NAVIGATION_PAGES.DRIVE
       || currentPage.value === NAVIGATION_PAGES.CASE_VIEW
       || currentPage.value === NAVIGATION_PAGES.NETWORK
       || currentPage.value === NAVIGATION_PAGES.NETWORK_FILES) {
        return true;
      }
      return !(driveStore.photoViewLayout === PHOTO_VIEW_LAYOUT.GRID) || props.blockGrid;
    });

    const authorPhotoUrl = computed(() => {
      if (props.photo.ownerId.internalId !== mainStore.user.accountId) {
        if (props.photo.owner.photo?.url) {
          return lowResUrlByViewTypeByUrl(props.photo.owner.photo.url, PHOTO_VIEW_TYPE.WHSML);
        }
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/profile.svg');
      }
      return '';
    });
    const authorName = computed(() => `${props.photo.owner.firstName} ${props.photo.owner.lastName}`);

    function selectPhoto(photo: IMedia): void {
      if (!props.selector) {
        return;
      }
      if (props.isParentSelect) {
        emit('on-select', props.photo.id);
      } else {
        // eslint-disable-next-line no-param-reassign
        photo.selected = !photo.selected;
      }
    }

    const isSelectedFromParent = computed(() => props.photo.id === props.selectedId);
    function openRenamePopUp(errorInfo: {title: string, error: string}) {
      renameMountErrorInfo.value = errorInfo;
    }

    function deletePhoto() {
      isQuestionPopUp.value = false;
      dataLoading.value = 'Deleting photo';
      driveStore.deleteMedia(props.photo.id, MEDIA_TYPES.PHOTO).finally(() => { dataLoading.value = ''; });
      // NotificationService.notifySuccess();
    }

    function viewPhoto(photo: IMedia): void {
      if (props.noClickAction) return;
      if (props.selector || props.preventOpen) {
        selectPhoto(photo);
        // eslint-disable-next-line no-useless-return
        return;
      }
      driveStore.appPhotoViewMedia = { ...photo, isMixed: props.isMixed };
    }

    return {
      mainStore,
      photoUrl,
      authorPhotoUrl,
      authorName,
      driveStore,
      isNetwork,
      isDeleteButton,
      isQuestionPopUp,
      isLoading,
      isSelectedFromParent,
      showTitle,
      reshareAllowed,
      selectPhoto,
      deletePhoto,
      viewPhoto,
      PHOTO_VIEW_LAYOUT,
      popUpSwitcher,
      isMediaSettingsPopUp,
      fileRenamePopUp,
      openRenamePopUp,
      renameMountErrorInfo,
      showMenuDots,
    };
  },
});
